/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: eslUserApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for ESL Users.
    Centralizes all API calls related to ESL Users.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListEslUsersResponse,
  EslUserResponse,
  EslUser,
} from '../types';

/**
 * Payload for creating an ESL user
 */
export interface CreateEslUserPayload {
  username: string;
  password: string;
  accessMode?: 'inbound' | 'outbound' | 'embedded';
  allowedIp?: string;
  listenIp?: string;
  listenPort?: number;
  natMap?: boolean;
  applyInboundAcl?: string | null;
  stopOnBindError?: boolean;
  enabled?: boolean;
  settings?: Record<string, unknown>;
  description?: string;
}

/**
 * Payload for updating an ESL user
 */
export interface UpdateEslUserPayload {
  username?: string;
  password?: string;
  accessMode?: 'inbound' | 'outbound' | 'embedded';
  allowedIp?: string;
  listenIp?: string;
  listenPort?: number;
  natMap?: boolean;
  applyInboundAcl?: string | null;
  stopOnBindError?: boolean;
  enabled?: boolean;
  settings?: Record<string, unknown>;
  description?: string;
}

/**
 * Fetch list of ESL users
 */
export async function fetchEslUsers(params: {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}): Promise<ListEslUsersResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    ...(params.sortBy && { sortBy: params.sortBy }),
    ...(params.sortOrder && { sortOrder: params.sortOrder }),
    ...(params.search && { search: params.search }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/esl-users?${queryParams}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to fetch ESL users');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch ESL users');
  }

  return result;
}

/**
 * Fetch all ESL users (for quick list)
 */
export async function fetchAllEslUsers(): Promise<EslUser[]> {
  const queryParams = new URLSearchParams({
    page: '1',
    limit: '1000',
    sortBy: 'username',
    sortOrder: 'asc',
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/esl-users?${queryParams}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to fetch ESL users');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch ESL users');
  }

  return result.data || [];
}

/**
 * Fetch single ESL user by ID
 */
export async function fetchEslUser(id: string | number): Promise<EslUserResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/esl-users/${id}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to fetch ESL user');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch ESL user');
  }

  return result;
}

/**
 * Create new ESL user
 */
export async function createEslUser(payload: CreateEslUserPayload): Promise<EslUserResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/esl-users'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to create ESL user');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create ESL user');
  }

  return result;
}

/**
 * Update ESL user
 */
export async function updateEslUser(id: string | number, payload: UpdateEslUserPayload): Promise<EslUserResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/esl-users/${id}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update ESL user');
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update ESL user');
  }

  return result;
}

/**
 * Delete ESL user
 */
export async function deleteEslUser(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/esl-users/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to delete ESL user');
  }
}

